import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Chip,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  DashboardCustomize as DashboardCustomizeIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import { ImageUploadService } from '../../accounting/inventory/services/imageUploadService';
import {
  getBlogSettings,
  saveBlogSettings,
  addBlogPost,
  updateBlogPost,
  deleteBlogPost,
  getProductModules,
  getGeneralConfig,
} from '../../../services/ecommerceApi';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import type { CorporateHeroSectionSettings } from '../../../types/corporateHero';
import { defaultCorporateHeroSectionSettings } from '../../../types/corporateHero';
import { buildCorporateHeroSavePayload, normalizeCorporateHeroSection } from '../../../utils/corporateHeroSettings';
import CorporateHeroSettingsPanel from './corporateHero/CorporateHeroSettingsPanel';
import GuideButton from '../../common/guide/GuideButton';
import {
  defaultBlogListingSettings,
  embedBlogListingInHeroJson,
  extractBlogListing,
  type BlogListingSettings,
} from '../utils/blogListingSettings';
import {
  DEFAULT_STOREFRONT_FONT,
  STOREFRONT_FONT_PRESETS,
  ensureStorefrontFontPreviewLoaded,
} from './storefrontFontPresets';

interface BlogPost {
  id: string;
  title: string;
  slug?: string;
  category?: string;
  excerpt?: string;
  content?: string;
  coverImageUrl?: string;
  bullets: string[];
  bulletsTitle?: string;
  meta: MetaItem[];
  badge?: string;
  isPublished: boolean;
  displayOrder: number;
}

interface MetaItem {
  label: string;
  value: string;
}

type HomeCardTemplate = 'featured' | 'media-archive';

interface HomeCardSettings {
  enabled: boolean;
  template: HomeCardTemplate;
  eyebrow: string;
  sectionTitle: string;
  sectionDescription: string;
  featuredPostId: string;
  smallCardCount: number;
  placement?: 'top' | 'bottom';
  insertAfter: string;
  insertPosition: 'before' | 'after';
  accentColor: string;
  titleColor: string;
  titleFontSize: number;
  /** Boşsa vitrinin site yazı tipi kullanılır. */
  titleFontFamily: string;
  backgroundColor: string;
  cardRadius: number;
  ctaText: string;
  smallCtaText: string;
}

const defaultHomeCard: HomeCardSettings = {
  enabled: true,
  template: 'featured',
  eyebrow: 'BLOG & İÇERİK',
  sectionTitle: 'Öne Çıkan İçerikler',
  sectionDescription:
    'Seçkin içeriklerimizle sizin için hazırladığımız yazılar, haberler ve ipuçları.',
  featuredPostId: '',
  smallCardCount: 4,
  placement: 'bottom',
  insertAfter: 'end',
  insertPosition: 'after',
  accentColor: '#1f5f43',
  titleColor: '#1c1917',
  titleFontSize: 36,
  titleFontFamily: '',
  backgroundColor: '',
  cardRadius: 16,
  ctaText: 'Devamını Oku',
  smallCtaText: 'Devamını Oku',
};

const STATIC_HOME_ANCHORS = [
  { value: 'slider', label: 'Slider' },
  { value: 'trust', label: 'Güven şeridi' },
  { value: 'categories', label: 'Kategoriler' },
  { value: 'end', label: 'Sayfa sonu' },
];

const normalizeHomeCard = (raw: Partial<HomeCardSettings> | null | undefined): HomeCardSettings => {
  const merged = { ...defaultHomeCard, ...(raw || {}) };
  if (!String(raw?.insertAfter || '').trim()) {
    merged.insertAfter = raw?.placement === 'top' ? 'categories' : 'end';
  }
  if (raw?.insertPosition !== 'before' && raw?.insertPosition !== 'after') {
    merged.insertPosition = 'after';
  }
  merged.template = raw?.template === 'media-archive' ? 'media-archive' : 'featured';
  merged.titleColor = raw?.titleColor || defaultHomeCard.titleColor;
  merged.titleFontSize =
    typeof raw?.titleFontSize === 'number' && Number.isFinite(raw.titleFontSize)
      ? raw.titleFontSize
      : defaultHomeCard.titleFontSize;
  merged.titleFontFamily = typeof raw?.titleFontFamily === 'string' ? raw.titleFontFamily : '';
  const cardCount = Number(raw?.smallCardCount);
  merged.smallCardCount = Number.isFinite(cardCount)
    ? Math.min(8, Math.max(0, Math.round(cardCount)))
    : defaultHomeCard.smallCardCount;
  merged.backgroundColor = raw?.backgroundColor || '';
  merged.cardRadius =
    typeof raw?.cardRadius === 'number' && Number.isFinite(raw.cardRadius)
      ? raw.cardRadius
      : defaultHomeCard.cardRadius;
  return merged;
};

interface BlogSettings {
  heroSection: CorporateHeroSectionSettings;
  listing: BlogListingSettings;
  homeCard: HomeCardSettings;
}

const defaultBlogHeroSection: CorporateHeroSectionSettings = {
  ...defaultCorporateHeroSectionSettings,
  title: 'Blog',
  description: 'En yeni içeriklerimiz, ipuçları ve haberler.',
  backgroundColor: 'linear-gradient(135deg, #1f5f43 0%, #2f8f66 100%)',
  height: 400,
  templateType: 'classic-gradient',
};

const defaultBlogSettings: BlogSettings = {
  heroSection: defaultBlogHeroSection,
  listing: defaultBlogListingSettings,
  homeCard: defaultHomeCard,
};

const emptyPost = (order: number): BlogPost => ({
  id: '',
  title: '',
  slug: '',
  category: '',
  excerpt: '',
  content: '',
  coverImageUrl: '',
  bullets: [],
  bulletsTitle: '',
  meta: [],
  badge: '',
  isPublished: true,
  displayOrder: order,
});

interface BlogSettingsComponentProps {
  companyId: string | null;
  configId: string;
}

const BlogSettingsComponent: React.FC<BlogSettingsComponentProps> = ({ companyId, configId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState<BlogSettings>(defaultBlogSettings);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [blogConfigId, setBlogConfigId] = useState<string | null>(null);
  const [homepageModules, setHomepageModules] = useState<Array<{ id: string; title: string }>>([]);
  const [siteFontFamily, setSiteFontFamily] = useState(DEFAULT_STOREFRONT_FONT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);

  const autoSaveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const resolvedTitleFont = settings.homeCard.titleFontFamily || siteFontFamily;

  useEffect(() => {
    ensureStorefrontFontPreviewLoaded();
    if (!companyId) return;
    let cancelled = false;
    getGeneralConfig(companyId)
      .then((response) => {
        const font = String(response?.data?.fontFamily || '').trim();
        if (!cancelled && font) setSiteFontFamily(font);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // ---- Load ----
  useEffect(() => {
    const load = async () => {
      if (!companyId || !configId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await getBlogSettings(companyId, configId);
        if (response?.success && response.data) {
          const data = response.data;
          setBlogConfigId(data.id);

          let homeCard = defaultHomeCard;
          if (data.homeCardSettingsJson) {
            try {
              homeCard = normalizeHomeCard(JSON.parse(data.homeCardSettingsJson));
            } catch {
              homeCard = defaultHomeCard;
            }
          }

          setSettings({
            heroSection: normalizeCorporateHeroSection(data, {
              title: data.heroTitle || defaultBlogHeroSection.title,
              description: data.heroDescription || defaultBlogHeroSection.description,
              backgroundColor: data.heroBackgroundColor || defaultBlogHeroSection.backgroundColor,
              height: data.heroHeight || defaultBlogHeroSection.height,
            }),
            listing: extractBlogListing(data.heroSettingsJson),
            homeCard,
          });

          setPosts((data.posts || []).map(mapApiPost));
        } else {
          setSettings(defaultBlogSettings);
          setPosts([]);
        }
      } catch (err) {
        console.error('❌ [BLOG] Ayarlar yüklenirken hata:', err);
        setError('Ayarlar yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [companyId, configId]);

  useEffect(() => {
    const loadModules = async () => {
      if (!companyId) return;
      try {
        const response = await getProductModules(companyId, 'custom');
        const rows = Array.isArray(response?.data) ? response.data : [];
        const modules = rows
          .map((module: any) => {
            let settings: any = {};
            try {
              settings = module.settingsJson ? JSON.parse(module.settingsJson) : {};
            } catch {
              settings = {};
            }
            if (settings?.enabled === false) return null;
            const title = String(module.sectionTitle || module.tabName || '').trim() || 'Modül';
            return { id: String(module.id), title, order: Number(module.displayOrder) || 0 };
          })
          .filter(Boolean)
          .sort((a: any, b: any) => a.order - b.order)
          .map((m: any) => ({ id: m.id, title: m.title }));
        setHomepageModules(modules);
      } catch (err) {
        console.error('❌ [BLOG] Anasayfa modülleri yüklenemedi:', err);
      }
    };
    loadModules();
  }, [companyId]);

  const mapApiPost = (p: any): BlogPost => {
    let bullets: string[] = [];
    if (p.bulletsJson) {
      try {
        const parsed = JSON.parse(p.bulletsJson);
        if (Array.isArray(parsed)) bullets = parsed.map((b: any) => String(b));
      } catch {
        bullets = [];
      }
    }
    let meta: MetaItem[] = [];
    let bulletsTitle = '';
    if (p.metaJson) {
      try {
        const parsed = JSON.parse(p.metaJson);
        bulletsTitle = parsed.bulletsTitle || '';
        if (Array.isArray(parsed.items)) {
          meta = parsed.items
            .map((m: any) => ({ label: String(m?.label || ''), value: String(m?.value || '') }))
            .filter((m: MetaItem) => m.label || m.value);
        } else if (parsed.prepTime || parsed.servings) {
          // Eski (tarif) formatından otomatik dönüşüm
          if (parsed.prepTime) meta.push({ label: 'Hazırlık', value: String(parsed.prepTime) });
          if (parsed.servings) meta.push({ label: 'Porsiyon', value: String(parsed.servings) });
        }
      } catch {
        /* ignore */
      }
    }
    return {
      id: p.id,
      title: p.title || '',
      slug: p.slug || '',
      category: p.category || '',
      excerpt: p.excerpt || '',
      content: p.content || '',
      coverImageUrl: p.coverImageUrl || '',
      bullets,
      bulletsTitle,
      meta,
      badge: p.badge || '',
      isPublished: p.isPublished ?? true,
      displayOrder: p.displayOrder ?? 0,
    };
  };

  // ---- Settings save (debounced) ----
  const buildSettingsPayload = (s: BlogSettings) => {
    const heroPayload = buildCorporateHeroSavePayload(s.heroSection);
    return {
      companyId,
      configId,
      ...heroPayload,
      heroSettingsJson: embedBlogListingInHeroJson(String(heroPayload.heroSettingsJson || '{}'), s.listing),
      homeCardSettingsJson: JSON.stringify(s.homeCard),
    };
  };

  const autoSaveSettings = async (s: BlogSettings) => {
    if (!companyId || !configId) return;
    try {
      const response = await saveBlogSettings(buildSettingsPayload(s));
      if (response?.success) {
        if (response.data?.id) setBlogConfigId(response.data.id);
        notifyEcommerceSaved();
      }
    } catch (err) {
      console.error('❌ [BLOG] Otomatik kayıt hatası:', err);
    }
  };

  const debouncedSaveSettings = (s: BlogSettings) => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => autoSaveSettings(s), 900);
  };

  const updateHeroSection = (heroSection: CorporateHeroSectionSettings) => {
    setSettings((prev) => {
      const next = { ...prev, heroSection };
      debouncedSaveSettings(next);
      return next;
    });
  };

  const updateListing = (patch: Partial<BlogListingSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, listing: { ...prev.listing, ...patch } };
      debouncedSaveSettings(next);
      return next;
    });
  };

  const updateHomeCard = (patch: Partial<HomeCardSettings>) => {
    setSettings((prev) => {
      const homeCard = { ...prev.homeCard, ...patch };
      if (homeCard.insertAfter === 'categories' && homeCard.insertPosition === 'after') {
        homeCard.placement = 'top';
      } else if (homeCard.insertAfter === 'end' && homeCard.insertPosition === 'after') {
        homeCard.placement = 'bottom';
      }
      const next = { ...prev, homeCard };
      debouncedSaveSettings(next);
      return next;
    });
  };

  const selectedAnchor = String(settings.homeCard.insertAfter || 'end');
  const homePositionOptions = [
    ...STATIC_HOME_ANCHORS.filter((item) => item.value !== 'end'),
    ...homepageModules.map((module) => ({
      value: module.id,
      label: module.title,
    })),
    ...(selectedAnchor &&
    selectedAnchor !== 'end' &&
    !STATIC_HOME_ANCHORS.some((item) => item.value === selectedAnchor) &&
    !homepageModules.some((module) => module.id === selectedAnchor)
      ? [{ value: selectedAnchor, label: 'Seçili bölüm' }]
      : []),
    STATIC_HOME_ANCHORS.find((item) => item.value === 'end')!,
  ];

  // ---- Ensure config exists (needed before adding posts) ----
  const ensureBlogConfigId = async (): Promise<string | null> => {
    if (blogConfigId) return blogConfigId;
    if (!companyId || !configId) return null;
    try {
      const response = await saveBlogSettings(buildSettingsPayload(settings));
      if (response?.success && response.data?.id) {
        setBlogConfigId(response.data.id);
        return response.data.id;
      }
    } catch (err) {
      console.error('❌ [BLOG] Config oluşturma hatası:', err);
    }
    return null;
  };

  // ---- Post dialog ----
  const handleAddPost = () => {
    setEditingPost(emptyPost(posts.length));
    setImageFile(null);
    setImagePreview(null);
    setEditDialogOpen(true);
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost({ ...post });
    setImageFile(null);
    setImagePreview(post.coverImageUrl || null);
    setEditDialogOpen(true);
  };

  const handleDeletePost = async (postId: string) => {
    if (!companyId) return;
    try {
      const response = await deleteBlogPost(postId, companyId);
      if (response?.success) {
        setPosts((prev) => prev.filter((p) => p.id !== postId));
        setSuccess('Yazı silindi');
        setTimeout(() => setSuccess(null), 2500);
      }
    } catch (err) {
      console.error('❌ [BLOG] Yazı silme hatası:', err);
      setError('Yazı silinirken bir hata oluştu');
    }
  };

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.size > 8 * 1024 * 1024) {
        setError('Görsel boyutu 8MB dan büyük olamaz');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const uploadCover = async (): Promise<string | null> => {
    if (!imageFile || !companyId) return null;
    try {
      setImageUploading(true);
      const [uploaded] = await ImageUploadService.uploadImages([imageFile]);
      if (!uploaded?.originalUrl) {
        throw new Error('Görsel servisi geçerli bir URL döndürmedi');
      }
      return uploaded.originalUrl;
    } catch (err: any) {
      console.error('❌ [BLOG] Kapak görseli yükleme hatası:', err);
      setError(err?.message || 'Kapak görseli yüklenirken bir hata oluştu');
      return null;
    } finally {
      setImageUploading(false);
    }
  };

  const handleSavePost = async () => {
    if (!editingPost) return;
    if (!editingPost.title.trim()) {
      setError('Başlık zorunludur');
      return;
    }

    const activeConfigId = await ensureBlogConfigId();
    if (!activeConfigId) {
      setError('Blog ayarları oluşturulamadı. Lütfen sayfayı yenileyip tekrar deneyin.');
      return;
    }

    try {
      let coverImageUrl = editingPost.coverImageUrl;
      if (imageFile) {
        const uploaded = await uploadCover();
        if (!uploaded) return;
        coverImageUrl = uploaded;
      }

      const payload = {
        blogConfigId: activeConfigId,
        companyId: companyId || '',
        title: editingPost.title,
        slug: editingPost.slug || '',
        category: editingPost.category,
        excerpt: editingPost.excerpt,
        content: editingPost.content,
        coverImageUrl,
        bulletsJson: JSON.stringify(editingPost.bullets.filter((b) => b.trim())),
        metaJson: JSON.stringify({
          bulletsTitle: editingPost.bulletsTitle || '',
          items: editingPost.meta.filter((m) => m.label.trim() || m.value.trim()),
        }),
        badge: editingPost.badge,
        isPublished: editingPost.isPublished,
        displayOrder: editingPost.displayOrder,
      };

      const isExisting = editingPost.id && posts.find((p) => p.id === editingPost.id);
      const response = isExisting
        ? await updateBlogPost(editingPost.id, payload)
        : await addBlogPost(payload);

      if (response?.success) {
        const saved = mapApiPost(response.data);
        setPosts((prev) => (isExisting ? prev.map((p) => (p.id === editingPost.id ? saved : p)) : [...prev, saved]));
        setEditDialogOpen(false);
        setEditingPost(null);
        setImageFile(null);
        setImagePreview(null);
        setSuccess(isExisting ? 'Yazı güncellendi' : 'Yazı eklendi');
        setTimeout(() => setSuccess(null), 2500);
      } else {
        setError(response?.message || 'Yazı kaydedilirken bir hata oluştu');
      }
    } catch (err) {
      console.error('❌ [BLOG] Yazı kaydetme hatası:', err);
      setError('Yazı kaydedilirken bir hata oluştu');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <ArticleIcon sx={{ mr: 1.5, color: 'primary.main' }} />
        <Typography variant="h6" fontWeight="bold">
          Blog
        </Typography>
        <GuideButton guideKey="ecommerce.blog" size="small" sx={{ ml: 0.5 }} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<ArticleIcon />} iconPosition="start" label="Blog Sayfası" />
        <Tab icon={<DashboardCustomizeIcon />} iconPosition="start" label="Anasayfa Kartı" />
      </Tabs>

      {/* ============ TAB 1: BLOG SAYFASI ============ */}
      {activeTab === 0 && (
        <Box>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="bold">
                Sayfa Hero Ayarları
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CorporateHeroSettingsPanel
                value={settings.heroSection}
                onChange={updateHeroSection}
                companyId={companyId}
                defaultHeight={400}
                sectionOptions={[{ label: 'Blog yazıları', value: 'blog-grid' }]}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                <ViewModuleIcon sx={{ mr: 1, color: 'primary.main' }} />
                Kart Listesi
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Arama ve mini filtre, blog sayfasında kartların hemen üstünde görünür.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Bir satırda kart sayısı</InputLabel>
                    <Select
                      label="Bir satırda kart sayısı"
                      value={settings.listing.cardsPerRow}
                      onChange={(e) => updateListing({ cardsPerRow: Number(e.target.value) })}
                    >
                      {[2, 3, 4].map((n) => (
                        <MenuItem key={n} value={n}>
                          {n} kart
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.listing.showSearch}
                        onChange={(e) => updateListing({ showSearch: e.target.checked })}
                      />
                    }
                    label="Arama kutusu"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.listing.showFilter}
                        onChange={(e) => updateListing({ showFilter: e.target.checked })}
                      />
                    }
                    label="Kategori filtresi"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="bold">
                Blog Yazıları ({posts.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 2 }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddPost}>
                  Yeni Yazı Ekle
                </Button>
              </Box>
              <Grid container spacing={2}>
                {posts.map((post) => (
                  <Grid item xs={12} md={6} lg={4} key={post.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {post.coverImageUrl && (
                        <Box sx={{ height: 140, overflow: 'hidden' }}>
                          <img
                            src={post.coverImageUrl}
                            alt={post.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </Box>
                      )}
                      <CardContent sx={{ flexGrow: 1 }}>
                        {post.category && (
                          <Chip size="small" label={post.category} color="primary" variant="outlined" sx={{ mb: 1 }} />
                        )}
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {post.title || 'Başlıksız'}
                        </Typography>
                        {post.excerpt && (
                          <Typography variant="body2" color="text.secondary">
                            {post.excerpt.substring(0, 90)}
                            {post.excerpt.length > 90 ? '…' : ''}
                          </Typography>
                        )}
                        {!post.isPublished && (
                          <Chip size="small" label="Taslak" color="warning" sx={{ mt: 1 }} />
                        )}
                      </CardContent>
                      <CardActions>
                        <IconButton size="small" onClick={() => handleEditPost(post)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeletePost(post.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
                {posts.length === 0 && (
                  <Grid item xs={12}>
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      Henüz blog yazısı eklenmemiş.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* ============ TAB 2: ANASAYFA KARTI ============ */}
      {activeTab === 1 && (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Kart şablonu
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {(
              [
                {
                  id: 'featured' as const,
                  label: 'Öne Çıkan Kart',
                  description: 'Büyük öne çıkan yazı ve altında küçük kartlar',
                },
                {
                  id: 'media-archive' as const,
                  label: 'Medya Arşivi',
                  description: 'Başlık solda, açıklama sağda, eşit video kartları',
                },
              ]
            ).map((option) => {
              const selected = settings.homeCard.template === option.id;
              return (
                <Grid item xs={12} sm={6} key={option.id}>
                  <Card
                    variant="outlined"
                    onClick={() => updateHomeCard({ template: option.id })}
                    sx={{
                      cursor: 'pointer',
                      borderWidth: selected ? 2 : 1,
                      borderColor: selected ? 'primary.main' : 'divider',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {settings.homeCard.template === 'media-archive' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Rozet, görselin üstündeki kanal adıdır. Süre için bir meta değerine 04:15 gibi bir zaman yazın.
              Kategoriyi virgül veya • ile ayırırsanız kartta iki etiket görünür. Kart görseli yazının kapak fotoğrafıdır.
            </Typography>
          )}
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Önizleme
          </Typography>
          <Box sx={{ mb: 3 }}>
            <BlogHomeCardPreview settings={settings.homeCard} posts={posts} titleFontFamily={resolvedTitleFont} />
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.homeCard.enabled}
                    onChange={(e) => updateHomeCard({ enabled: e.target.checked })}
                  />
                }
                label="Anasayfada Göster"
              />
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Üst Etiket (Eyebrow)"
                value={settings.homeCard.eyebrow}
                onChange={(e) => updateHomeCard({ eyebrow: e.target.value })}
              />
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Bölüm Başlığı"
                value={settings.homeCard.sectionTitle}
                onChange={(e) => updateHomeCard({ sectionTitle: e.target.value })}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                sx={{ mt: 2 }}
                label="Bölüm Açıklaması"
                value={settings.homeCard.sectionDescription}
                onChange={(e) => updateHomeCard({ sectionDescription: e.target.value })}
              />
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>
                  {settings.homeCard.template === 'media-archive' ? 'İlk kart' : 'Öne Çıkan Yazı'}
                </InputLabel>
                <Select
                  label={settings.homeCard.template === 'media-archive' ? 'İlk kart' : 'Öne Çıkan Yazı'}
                  value={settings.homeCard.featuredPostId}
                  onChange={(e) => updateHomeCard({ featuredPostId: String(e.target.value) })}
                >
                  <MenuItem value="">
                    <em>En yeni yazı (otomatik)</em>
                  </MenuItem>
                  {posts.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.title || 'Başlıksız'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>
                  {settings.homeCard.template === 'media-archive' ? 'Kart sayısı' : 'Alttaki kart sayısı'}
                </InputLabel>
                <Select
                  label={settings.homeCard.template === 'media-archive' ? 'Kart sayısı' : 'Alttaki kart sayısı'}
                  value={settings.homeCard.smallCardCount}
                  onChange={(e) => updateHomeCard({ smallCardCount: Number(e.target.value) })}
                >
                  {(settings.homeCard.template === 'media-archive'
                    ? [1, 2, 3, 4, 5, 6, 8]
                    : [0, 1, 2, 3, 4, 5, 6]
                  ).map((n) => (
                    <MenuItem key={n} value={n}>
                      {n === 0 ? 'Yok — yalnız öne çıkan kart' : `${n} kart`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {settings.homeCard.template === 'media-archive'
                  ? 'Anasayfada bu kadar kart görünür.'
                  : 'Öne çıkan büyük kart bu sayıya dahil değildir. 0 seçerseniz anasayfada yalnız o kart kalır.'}
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Yazı tipi</InputLabel>
                <Select
                  label="Yazı tipi"
                  value={settings.homeCard.titleFontFamily || ''}
                  onChange={(e) => updateHomeCard({ titleFontFamily: String(e.target.value) })}
                >
                  <MenuItem value="">Site yazı tipi</MenuItem>
                  {STOREFRONT_FONT_PRESETS.map((preset) => (
                    <MenuItem key={preset.key} value={preset.value} sx={{ fontFamily: preset.value }}>
                      {preset.value.split(',')[0].replace(/"/g, '')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Varsayılan, sitenin genel yazı tipidir. Bölüm başlığı, kart başlıkları ve yazı sayfası bu tipi kullanır.
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Hangi bölümün yanında</InputLabel>
                <Select
                  label="Hangi bölümün yanında"
                  value={selectedAnchor}
                  onChange={(e) => updateHomeCard({ insertAfter: String(e.target.value) })}
                >
                  {homePositionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Konum</InputLabel>
                <Select
                  label="Konum"
                  value={settings.homeCard.insertPosition === 'before' ? 'before' : 'after'}
                  onChange={(e) =>
                    updateHomeCard({ insertPosition: e.target.value as 'before' | 'after' })
                  }
                >
                  <MenuItem value="before">Bu bölümden önce</MenuItem>
                  <MenuItem value="after">Bu bölümden sonra</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Blog kartı, seçilen anasayfa bölümünün hemen önünde veya hemen ardından gösterilir.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Vurgu Rengi"
                    value={settings.homeCard.accentColor}
                    onChange={(e) => updateHomeCard({ accentColor: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Öne Çıkan Buton Metni"
                    value={settings.homeCard.ctaText}
                    onChange={(e) => updateHomeCard({ ctaText: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Küçük Kart Buton Metni"
                    value={settings.homeCard.smallCtaText}
                    onChange={(e) => updateHomeCard({ smallCtaText: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Başlık rengi"
                    value={settings.homeCard.titleColor}
                    onChange={(e) => updateHomeCard({ titleColor: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Başlık boyutu (px)"
                    value={settings.homeCard.titleFontSize}
                    onChange={(e) => updateHomeCard({ titleFontSize: Number(e.target.value) || 36 })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Zemin rengi"
                    value={settings.homeCard.backgroundColor}
                    onChange={(e) => updateHomeCard({ backgroundColor: e.target.value })}
                    placeholder={settings.homeCard.template === 'media-archive' ? '#f6f1e8' : 'boş = sayfa zemini'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Kart şekli</InputLabel>
                    <Select
                      label="Kart şekli"
                      value={settings.homeCard.cardRadius}
                      onChange={(e) => updateHomeCard({ cardRadius: Number(e.target.value) })}
                    >
                      <MenuItem value={4}>Köşeli</MenuItem>
                      <MenuItem value={16}>Yuvarlatılmış</MenuItem>
                      <MenuItem value={28}>Yumuşak</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ============ POST EDIT DIALOG ============ */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPost?.id && posts.find((p) => p.id === editingPost.id) ? 'Yazıyı Düzenle' : 'Yeni Yazı'}</DialogTitle>
        <DialogContent>
          {editingPost && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Başlık"
                  value={editingPost.title}
                  onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Slug (opsiyonel)"
                  value={editingPost.slug}
                  onChange={(e) => setEditingPost({ ...editingPost, slug: e.target.value })}
                  placeholder="otomatik oluşturulur"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Kategori / Üst Etiket"
                  value={editingPost.category}
                  onChange={(e) => setEditingPost({ ...editingPost, category: e.target.value })}
                  placeholder="Örn: İTALYAN KLASİĞİ"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Rozet (opsiyonel)"
                  value={editingPost.badge}
                  onChange={(e) => setEditingPost({ ...editingPost, badge: e.target.value })}
                  placeholder="Örn: AYIN SEÇKİN TARİFİ • 22 AĞUSTOS"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Özet / Kart Açıklaması"
                  value={editingPost.excerpt}
                  onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                />
              </Grid>
              {/* Dinamik meta bilgileri (etiket + değer) — her konuya uyar */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Ek Bilgiler
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setEditingPost({ ...editingPost, meta: [...editingPost.meta, { label: '', value: '' }] })
                    }
                  >
                    Satır Ekle
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  İstediğin etiket-değer çiftini ekle. Örn: "Hazırlık → 20 Dk", "Kapasite → 500 ton/gün", "Menzil → 3.000 km".
                </Typography>
                {editingPost.meta.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Henüz ek bilgi eklenmedi.
                  </Typography>
                )}
                {editingPost.meta.map((m, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      label="Etiket"
                      value={m.label}
                      onChange={(e) => {
                        const meta = [...editingPost.meta];
                        meta[idx] = { ...meta[idx], label: e.target.value };
                        setEditingPost({ ...editingPost, meta });
                      }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Değer"
                      value={m.value}
                      onChange={(e) => {
                        const meta = [...editingPost.meta];
                        meta[idx] = { ...meta[idx], value: e.target.value };
                        setEditingPost({ ...editingPost, meta });
                      }}
                      sx={{ flex: 1.5 }}
                    />
                    <IconButton
                      color="error"
                      onClick={() =>
                        setEditingPost({ ...editingPost, meta: editingPost.meta.filter((_, i) => i !== idx) })
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Liste Başlığı (opsiyonel)"
                  value={editingPost.bulletsTitle}
                  onChange={(e) => setEditingPost({ ...editingPost, bulletsTitle: e.target.value })}
                  placeholder="Örn: Gerekli Malzemeler, Teknik Özellikler"
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Madde Listesi (her satır bir madde)"
                  value={editingPost.bullets.join('\n')}
                  onChange={(e) => setEditingPost({ ...editingPost, bullets: e.target.value.split('\n') })}
                  helperText="Kart ve yazı sayfasında gösterilecek maddeler"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="İçerik (HTML destekler)"
                  value={editingPost.content}
                  onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <Typography gutterBottom>Kapak Görseli</Typography>
                <Button variant="outlined" component="label" startIcon={<ImageIcon />}>
                  Görsel Seç
                  <input type="file" hidden accept="image/*" onChange={handleImageFileChange} />
                </Button>
                {imagePreview && (
                  <Box sx={{ mt: 2, borderRadius: 1, overflow: 'hidden', maxWidth: 320 }}>
                    <img src={imagePreview} alt="Önizleme" style={{ width: '100%', objectFit: 'cover' }} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingPost.isPublished}
                      onChange={(e) => setEditingPost({ ...editingPost, isPublished: e.target.checked })}
                    />
                  }
                  label="Yayınla"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Sıra"
                  value={editingPost.displayOrder}
                  onChange={(e) => setEditingPost({ ...editingPost, displayOrder: Number(e.target.value) })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={handleSavePost} disabled={imageUploading || !editingPost?.title.trim()}>
            {imageUploading ? 'Yükleniyor…' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ---- Static preview mimicking the homepage card ----
const BlogHomeCardPreview: React.FC<{
  settings: HomeCardSettings;
  posts: BlogPost[];
  titleFontFamily: string;
}> = ({ settings, posts, titleFontFamily }) => {
  const featured =
    posts.find((p) => p.id === settings.featuredPostId) || posts[0] || null;
  const smalls = posts.filter((p) => !featured || p.id !== featured.id).slice(0, settings.smallCardCount);

  if (settings.template === 'media-archive') {
    const ordered = featured ? [featured, ...posts.filter((post) => post.id !== featured.id)] : posts;
    const cards = ordered.slice(0, Math.max(1, settings.smallCardCount || 3));
    const placeholders = cards.length
      ? cards
      : Array.from({ length: settings.smallCardCount || 3 }, () => null);
    return (
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: settings.backgroundColor || '#f6f1e8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
          <Box>
            <Typography variant="caption" sx={{ letterSpacing: 1, color: settings.accentColor, fontWeight: 700 }}>
              {settings.eyebrow}
            </Typography>
            <Typography sx={{ fontFamily: titleFontFamily, fontWeight: 700, color: settings.titleColor, fontSize: Math.min(settings.titleFontSize, 32), lineHeight: 1.15 }}>
              {settings.sectionTitle}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 140 }}>
            {settings.sectionDescription}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(Math.max(placeholders.length, 1), 3)}, 1fr)`,
            gap: 1,
          }}
        >
          {placeholders.map((post, index) => (
            <Box key={post?.id || index} sx={{ bgcolor: '#fff', borderRadius: `${settings.cardRadius}px`, p: 0.75, overflow: 'hidden' }}>
              <Box
                sx={{
                  height: 110,
                  borderRadius: `${Math.max(4, settings.cardRadius - 6)}px`,
                  bgcolor: '#d7cfc3',
                  backgroundImage: post?.coverImageUrl ? `url(${post.coverImageUrl})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    bgcolor: '#fff',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              </Box>
              <Typography variant="caption" fontWeight="bold" noWrap display="block" sx={{ mt: 0.5, fontFamily: titleFontFamily }}>
                {post?.title || 'Haber kaydı'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {settings.smallCtaText || settings.ctaText || 'Haber Kaydını İzle'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: settings.backgroundColor || '#f4f7f5' }}>
      <Typography variant="caption" sx={{ letterSpacing: 1, color: settings.accentColor, fontWeight: 700 }}>
        {settings.eyebrow}
      </Typography>
      <Typography sx={{ fontFamily: titleFontFamily, fontWeight: 700, color: settings.titleColor, fontSize: Math.min(settings.titleFontSize, 32), lineHeight: 1.15 }}>
        {settings.sectionTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {settings.sectionDescription}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, bgcolor: '#fff', borderRadius: `${settings.cardRadius}px`, overflow: 'hidden', boxShadow: 1 }}>
        <Box
          sx={{
            width: 130,
            minHeight: 120,
            bgcolor: '#dfe7e2',
            backgroundImage: featured?.coverImageUrl ? `url(${featured.coverImageUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <Box sx={{ p: 1.5, flex: 1 }}>
          <Typography variant="caption" sx={{ color: settings.accentColor, fontWeight: 700 }}>
            {featured?.category || 'KATEGORİ'}
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: titleFontFamily }}>
            {featured?.title || 'Öne Çıkan Yazı'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {featured?.excerpt?.substring(0, 70) || 'Öne çıkan yazı açıklaması burada görünür.'}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: settings.smallCardCount === 1 ? '1fr' : 'repeat(2, 1fr)',
            md: `repeat(${Math.min(Math.max(settings.smallCardCount, 1), 4)}, 1fr)`,
          },
          gap: 1,
          mt: 1.5,
        }}
      >
        {(smalls.length ? smalls : Array.from({ length: settings.smallCardCount })).map((p: any, i) => (
          <Box key={p?.id || i} sx={{ bgcolor: '#fff', borderRadius: 1.5, overflow: 'hidden', boxShadow: 1 }}>
            <Box
              sx={{
                height: 48,
                bgcolor: '#dfe7e2',
                backgroundImage: p?.coverImageUrl ? `url(${p.coverImageUrl})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" fontWeight="bold" noWrap display="block" sx={{ fontFamily: titleFontFamily }}>
                {p?.title || 'Yazı'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BlogSettingsComponent;
